import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { AdminClient } from "../../../api/generated/APIClient";
import { EntityImageThumbnail } from "../../../components/ui/EntityImageThumbnail";
import { AnnounceDetailPage } from "./AnnounceDetailPage";
import { AnnounceFormPage } from "./AnnounceFormPage";
import "./AnnouncePage.css";

// ルートモードの型
type RouteMode = "list" | "detail" | "edit" | "create";

export const AnnouncePage: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [announces, setAnnounces] = useState<any[]>([]);
  const [currentAnnounce, setCurrentAnnounce] = useState<any | undefined>();

  // ルートからモードを判定
  const getMode = (): RouteMode => {
    const path = location.pathname;
    if (path.includes("/edit")) return "edit";
    if (path.includes("/create")) return "create";
    if (id) return "detail";
    return "list";
  };

  const mode = getMode();

  // Connect-ES APIクライアント呼び出し関数（システムエンティティ用）
  const callSystemEntityAPI = async (
    operation: "index" | "create" | "update" | "delete",
    data?: any,
    ids?: string[]
  ) => {
    try {
      // エンティティ名をPascalCaseに変換（Connect-ESメソッド名に合わせる）
      const entityPascalCase = "Announce";

      // APIメソッド名を構築（Connect-ESの命名規則に従う）
      let methodName: string;
      switch (operation) {
        case "index":
          methodName = `Index${entityPascalCase}`;
          break;
        case "create":
          methodName = `Create${entityPascalCase}`;
          break;
        case "update":
          methodName = `Update${entityPascalCase}`;
          break;
        case "delete":
          methodName = `Delete${entityPascalCase}`;
          break;
        default:
          throw new Error(`未対応の操作: ${operation}`);
      }

      // AdminClientのメソッドを取得
      const apiMethod = (AdminClient as any)[methodName];
      if (!apiMethod || typeof apiMethod !== "function") {
        throw new Error(`APIメソッド ${methodName} が見つかりません`);
      }

      // API引数を構築（システムエンティティ用）
      let apiArgs: any = {};
      if (operation === "index") {
        // システムエンティティはUserIDを必要としない
        apiArgs = {
          offset: 0,
          limit: 0,
        };
      } else if (operation === "delete" && ids) {
        apiArgs = {
          ids: ids,
        };
      } else if (data) {
        // create/updateの場合は適切なフィールド名でデータを送信
        const entityArrayKey = `announces`;
        apiArgs = {
          [entityArrayKey]: [data],
        };
      }

      // Connect-ES APIを呼び出し
      const result = await apiMethod(apiArgs);
      return result;
    } catch (error) {
      console.error(`❌ ${operation} request failed (System Entity):`, error);
      throw error;
    }
  };

  useEffect(() => {
    // Connect-ES APIからシステムエンティティデータを取得
    const fetchAnnounces = async () => {
      try {
        const response = await callSystemEntityAPI("index");
        // Connect-ESレスポンスの構造を解析
        let items: any[] = [];
        if (response && response.data) {
          if (Array.isArray(response.data)) {
            items = response.data;
          } else if (typeof response.data === "object") {
            const dataObj = response.data as any;
            // Connect-ESの一般的なレスポンス構造を確認
            if (dataObj.announces && Array.isArray(dataObj.announces)) {
              items = dataObj.announces;
            } else if (dataObj.items && Array.isArray(dataObj.items)) {
              items = dataObj.items;
            } else if (dataObj.announces === null || dataObj.items === null) {
              // nullの場合は空配列を設定
              items = [];
            } else if (Object.keys(dataObj).length === 0) {
              // 空のオブジェクトの場合は空配列を設定
              items = [];
            } else if (dataObj.id) {
              // 有効なIDを持つ単一オブジェクトの場合は配列に変換
              items = [dataObj];
            } else {
              // IDがない無効なオブジェクトの場合は空配列
              items = [];
            }
          }
        }
        setAnnounces(items);
      } catch (error) {
        console.error("❌ Announce取得エラー (System Entity):", error);
        // エラー時は空配列を設定
        setAnnounces([]);
      }
    };

    fetchAnnounces();
  }, []);

  useEffect(() => {
    // 現在のアイテムを設定
    if (id) {
      const item = announces.find((item) => item.id === id);
      setCurrentAnnounce(item);
    } else {
      setCurrentAnnounce(undefined);
    }
  }, [id, announces]);

  const handleEdit = (itemId: string) => {
    navigate(`/admin/entities/announce/${itemId}/edit`);
  };

  const handleDelete = async (itemId: string) => {
    if (!confirm("お知らせを削除しますか？")) {
      return;
    }

    try {
      // Connect-ES API削除リクエストを送信
      await callSystemEntityAPI("delete", undefined, [itemId]);

      // ローカル状態を更新
      setAnnounces(announces.filter((item) => item.id !== itemId));

      // 詳細ページから削除した場合は一覧ページに移動
      if (id === itemId) {
        navigate(`/admin/entities/announce`);
      }
    } catch (error) {
      console.error("❌ 削除エラー:", error);
      alert(
        `削除に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    }
  };

  const handleCreate = () => {
    navigate(`/admin/entities/announce/create`);
  };

  const handleBack = () => {
    navigate(`/admin/entities/announce`);
  };

  const handleDetail = (itemId: string) => {
    navigate(`/admin/entities/announce/${itemId}`);
  };

  // 作成・編集モードの場合は専用フォームページを表示
  if (mode === "create" || mode === "edit") {
    return <AnnounceFormPage />;
  }

  // 一覧モードの場合はカスタム表示
  if (mode === "list") {
    return (
      <div className="announce-list-page">
        <div className="announce-list-header">
          <h1>お知らせ一覧</h1>
          <div className="announce-list-actions">
            <button className="btn btn-primary" onClick={handleCreate}>
              新規作成
            </button>
          </div>
        </div>

        <div className="announce-list-content">
          {announces.length === 0 ? (
            <div className="announce-empty-state">
              <p>お知らせがありません</p>
              <button className="btn btn-primary" onClick={handleCreate}>
                新規作成
              </button>
            </div>
          ) : (
            <div className="announce-list-table-container">
              <table className="announce-list-table">
                <thead>
                  <tr>
                    <th className="col-image">サムネイル</th>
                    <th className="col-title">タイトル</th>
                    <th className="col-date">公開日</th>
                    <th className="col-priority">優先度</th>
                    <th className="col-status">状態</th>
                    <th className="col-actions">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {announces.map((announce) => (
                    <tr key={announce.id}>
                      <td className="col-image">
                        <EntityImageThumbnail
                          storagePath={
                            announce.thumbnailImage?.storagePath ||
                            announce.thumbnailImage?.storage_path ||
                            announce.thumbnail_image?.storagePath ||
                            announce.thumbnail_image?.storage_path
                          }
                          alt={announce.title}
                          size={48}
                          onClick={() => handleDetail(announce.id)}
                        />
                      </td>
                      <td className="col-title">
                        <Link
                          to={`/admin/entities/announce/${announce.id}`}
                          className="announce-title-link"
                        >
                          {announce.title}
                        </Link>
                      </td>
                      <td className="col-date">
                        {formatPublishDate(
                          announce.publishDate || announce.publish_date
                        )}
                      </td>
                      <td className="col-priority">
                        {announce.displayPriority ?? announce.display_priority ?? 0}
                      </td>
                      <td className="col-status">
                        <span
                          className={`status-badge ${
                            announce.isPublished ?? announce.is_published
                              ? "enabled"
                              : "disabled"
                          }`}
                        >
                          {announce.isPublished ?? announce.is_published
                            ? "公開"
                            : "非公開"}
                        </span>
                      </td>
                      <td className="col-actions">
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleDetail(announce.id)}
                          >
                            詳細
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEdit(announce.id)}
                          >
                            編集
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(announce.id)}
                          >
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 詳細モードの場合は専用詳細ページを使用
  return (
    <AnnounceDetailPage
      announce={currentAnnounce}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onBack={handleBack}
    />
  );
};

// 公開日をフォーマット
function formatPublishDate(publishDate: string): string {
  if (!publishDate) return "-";
  const date = new Date(publishDate);
  if (isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}
